import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Skeleton,
  LinearProgress,
  IconButton,
  Tooltip as MuiTooltip,
  alpha,
} from "@mui/material";
import {
  Refresh,
  Download,
  WarningAmber,
  HealthAndSafety,
  Public,
  FilterList,
  CheckCircle,
  ErrorOutline,
  NotificationsNone,
  FiberManualRecord,
  TrendingUp,
  TrendingDown,
  PersonOutline,
  AirplanemodeActive,
  LocationOn,
  Assessment,
  GppBad,
  Biotech,
  WifiTethering,
  SyncAlt,
  KeyboardArrowUp,
  KeyboardArrowDown,
  Circle,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart as ReBarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  Area,
  AreaChart,
  ReferenceLine,
} from "recharts";
import { format, subDays } from "date-fns";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import { reportsAPI } from "../services/api";
import { LANG_STORAGE_KEY } from "../i18n";
import GeoDashboardMap from "../components/Dashboard/GeoDashboardMap";

/* ═══════════════════════════════════════════════════════════════════
   DESIGN SYSTEM
══════════════════════════════════════════════════════════════════ */
const C = {
  pageBg: "#f1f5f9",
  headerBg: "#0f1f35",
  headerBorder: "rgba(255,255,255,0.06)",
  card: "#ffffff",
  cardBorder: "#e2e8f0",
  cardHover: "#f8fafc",
  ink: "#0f1f35",
  sub: "#475569",
  muted: "#94a3b8",
  onDark: "rgba(255,255,255,0.90)",
  onDarkMuted: "rgba(255,255,255,0.45)",
  teal: "#0d9488",
  tealDk: "#0f766e",
  tealLt: "#ccfbf1",
  green: "#16a34a",   greenLt: "#dcfce7",
  red: "#dc2626",     redLt: "#fef2f2",
  amber: "#d97706",   amberLt: "#fef3c7",
  blue: "#2563eb",    blueLt: "#dbeafe",
  purple: "#7c3aed",  purpleLt: "#ede9fe",
  orange: "#ea580c",  orangeLt: "#fff7ed",
  gray: "#94a3b8",
  divider: "#f1f5f9",
};

const F = {
  sans: "'Inter', 'Helvetica Neue', sans-serif",
  mono: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
};

const S = {
  sm: "0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)",
  md: "0 4px 16px rgba(0,0,0,0.08)",
  lg: "0 8px 32px rgba(0,0,0,0.11)",
  xl: "0 20px 60px rgba(0,0,0,0.14)",
};

const card = (extra?: object) => ({
  background: C.card,
  border: `1px solid ${C.cardBorder}`,
  borderRadius: "16px",
  boxShadow: S.sm,
  ...extra,
});

/* ═══════════════════════════════════════════════════════════════════
   DATA CONFIG
══════════════════════════════════════════════════════════════════ */
const KPI_CONFIG = [
  { tKey: "ebolaDashboard.kpi.totalScreeningsToday", key: "total_screenings",   icon: PersonOutline,      color: C.blue,   bg: C.blueLt,   delta_key: "total_screenings_delta", suffixKey: "ebolaDashboard.kpi.vsYesterday"  },
  { tKey: "ebolaDashboard.kpi.clearedPassed",        key: "cleared",            icon: CheckCircle,        color: C.green,  bg: C.greenLt,  pct_key: "cleared_pct",              suffixKey: "ebolaDashboard.kpi.ofTotal"      },
  { tKey: "ebolaDashboard.kpi.highRiskAlerts",       key: "high_risk", icon: WarningAmber,       color: C.red,    bg: C.redLt,    pct_key: "high_risk_pct", suffixKey: "ebolaDashboard.kpi.ofTotal"      },
  { tKey: "ebolaDashboard.kpi.suspectedCases",       key: "high_risk", icon: Biotech,            color: C.orange, bg: C.orangeLt, pct_key: "high_risk_pct", suffixKey: "ebolaDashboard.kpi.ofTotal"      },
  { tKey: "ebolaDashboard.kpi.activePortsOfEntry",   key: "active_ports",       icon: AirplanemodeActive, color: C.purple, bg: C.purpleLt, total_key: "total_ports" },
  { tKey: "ebolaDashboard.kpi.referredFollowup",     key: "high_risk", icon: FilterList,         color: C.amber,  bg: C.amberLt,  pct_key: "high_risk_pct", suffixKey: "ebolaDashboard.kpi.ofTotal"      },
];

const RISK_COLORS: Record<string, string> = {
  "High Risk": C.red, "Moderate": C.amber, "Low Risk": C.green,
};

const numFmt  = (v: number | undefined | null) => (v ?? 0).toLocaleString();
const riskClr = (s: string = "") => s.toLowerCase().includes("high") ? C.red : s.toLowerCase().includes("mod") ? C.amber : C.green;
const riskBgClr = (s: string = "") => riskClr(s) + "1a";

/* ═══════════════════════════════════════════════════════════════════
   SUB-COMPONENTS
══════════════════════════════════════════════════════════════════ */

const LivePulse: React.FC<{ color?: string }> = ({ color = C.green }) => (
  <Box sx={{ position: "relative", width: 8, height: 8, flexShrink: 0 }}>
    <Box sx={{
      width: 8, height: 8, borderRadius: "50%", background: color, position: "absolute",
      "@keyframes pulse": {
        "0%":   { boxShadow: `0 0 0 0 ${color}66` },
        "70%":  { boxShadow: `0 0 0 6px ${color}00` },
        "100%": { boxShadow: `0 0 0 0 ${color}00`  },
      },
      animation: "pulse 2s infinite",
    }} />
  </Box>
);

const AnimNumber: React.FC<{ value: number; duration?: number }> = ({ value, duration = 800 }) => {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const start = prev.current;
    const end = value;
    const startTime = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(start + (end - start) * eased));
      if (t < 1) requestAnimationFrame(tick);
      else prev.current = end;
    };
    requestAnimationFrame(tick);
  }, [value, duration]);
  return <>{numFmt(display)}</>;
};

const ChartTip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      background: C.headerBg, color: C.onDark, px: 2, py: 1.5, borderRadius: "10px",
      boxShadow: S.lg, border: "1px solid rgba(255,255,255,0.08)", fontFamily: F.mono,
    }}>
      <Typography sx={{ fontSize: 10, color: C.onDarkMuted, mb: 0.8, fontFamily: F.mono }}>{label}</Typography>
      {payload.map((p: any) => (
        <Box key={p.dataKey} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: p.color }} />
          <Typography sx={{ fontSize: 11, fontFamily: F.mono, color: C.onDark }}>
            {p.name}: <strong>{numFmt(p.value)}</strong>
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const SectionTitle: React.FC<{ title: string; sub?: string; right?: React.ReactNode }> = ({ title, sub, right }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
    <Box>
      <Typography sx={{ fontFamily: F.sans, fontWeight: 700, fontSize: 14, color: C.ink, lineHeight: 1.3 }}>{title}</Typography>
      {sub && <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.muted, mt: 0.3 }}>{sub}</Typography>}
    </Box>
    {right}
  </Box>
);

const RiskBadge: React.FC<{ label: string }> = ({ label }) => (
  <Chip label={label} size="small" sx={{
    bgcolor: riskBgClr(label), color: riskClr(label),
    fontFamily: F.mono, fontSize: 10, fontWeight: 700,
    height: 20, borderRadius: "5px",
    border: `1px solid ${riskClr(label)}30`,
  }} />
);

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════ */
const DashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [lang, setLang] = useState(localStorage.getItem(LANG_STORAGE_KEY) || 'en');
  const handleLangChange = (newLang: string) => {
    setLang(newLang);
    i18n.changeLanguage(newLang);
    localStorage.setItem(LANG_STORAGE_KEY, newLang);
  };

  const [data,         setData        ] = useState<any>(null);
  const [loading,      setLoading     ] = useState(true);
  const [error,        setError       ] = useState<string | null>(null);
  const [startDate,    setStartDate   ] = useState<Date>(subDays(new Date(), 0));
  const [endDate,      setEndDate     ] = useState<Date>(new Date());
  const [selectedPort, setSelectedPort] = useState("ALL");
  const [selectedRisk, setSelectedRisk] = useState("ALL");
  const [activeKpi,    setActiveKpi   ] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const result = await reportsAPI.getStats({
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date:   format(endDate,   "yyyy-MM-dd"),
        port:       selectedPort !== "ALL" ? selectedPort : undefined,
        risk_level: selectedRisk !== "ALL" ? selectedRisk : undefined,
      });
      setData(result);
    } catch (e: any) {
      setError(e?.message || t('ebolaDashboard.errors.loadFailed'));
    } finally { setLoading(false); }
  }, [startDate, endDate, selectedPort, selectedRisk]);

  useEffect(() => {
    fetchData();
    const id = window.setInterval(fetchData, 30_000);
    return () => window.clearInterval(id);
  }, [fetchData]);

  const exportData = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([data.summary || {}]), "Summary");
    if (Array.isArray(data.ports))         XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.ports),          "Ports");
    if (Array.isArray(data.latest_alerts)) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.latest_alerts), "Alerts");
    XLSX.writeFile(wb, `ebola_dashboard_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
  };

  const summary = data?.summary || {};
  const snap    = data?.operational_snapshot || {};

  const portOptions = useMemo(() => {
    const s = new Set<string>();
    (data?.ports || []).forEach((p: any) => { const n = p?.port_name || p?.port; if (n) s.add(n); });
    return ["ALL", ...Array.from(s)];
  }, [data]);

  const trendData = useMemo(() =>
    (data?.screening_trend || []).map((i: any) => ({
      date: i.date,
      screenings:       i.screenings       || 0,
      high_risk_alerts: i.high_risk_alerts || 0,
    })), [data]);

  const riskChartData = useMemo(() =>
    (data?.risk_distribution || []).map((i: any) => ({
      name: i.risk_level, value: i.count, color: RISK_COLORS[i.risk_level] || C.gray,
    })), [data]);

  const outcomeByPort = useMemo(() =>
    (data?.ports || []).slice(0, 6).map((p: any) => ({
      port:      (p.port_name || p.port || "").split(" ").slice(0, 2).join(" "),
      cleared:   p.cleared   || 0,
      high_risk: p.high_risk || 0,
      follow_up: p.suspected || 0,
    })), [data]);

  const countryData = useMemo(() => (data?.country_distribution || []).slice(0, 6), [data]);
  const symptomData = useMemo(() => (data?.symptom_distribution  || []).slice(0, 8), [data]);
  const vaccData    = useMemo(() => (data?.vaccination_distribution || []).map((i: any) => ({ name: i.status, value: i.count })), [data]);

  const vaccPct = useMemo(() => {
    const total = vaccData.reduce((a: number, b: any) => a + b.value, 0);
    const v = vaccData.find((d: any) => d.name?.toLowerCase().includes("vacc"))?.value || 0;
    return total ? Math.round((v / total) * 100) : 0;
  }, [vaccData]);

  const selectedCountry = useMemo(() => {
    const l = data?.country_distribution?.[0]?.country || "";
    return l.toLowerCase().includes("switz") ? "CH" : "CAR";
  }, [data]);

  const trendDelta = useMemo(() => {
    if (trendData.length < 2) return null;
    const last = trendData[trendData.length - 1].screenings;
    const prev = trendData[trendData.length - 2].screenings;
    if (!prev) return null;
    return Math.round(((last - prev) / prev) * 1000) / 10;
  }, [trendData]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ minHeight: "100vh", background: C.pageBg, fontFamily: F.sans }}>

        {/* ══ HEADER ══════════════════════════════════════════════ */}
        <Box sx={{
          background: C.headerBg,
          borderBottom: `1px solid ${C.headerBorder}`,
          position: "sticky", top: 0, zIndex: 100,
          boxShadow: "0 2px 20px rgba(0,0,0,0.25)",
        }}>
          <Box sx={{ maxWidth: 1600, mx: "auto", px: 3, py: 0, display: "flex", alignItems: "stretch", minHeight: 64 }}>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pr: 3, borderRight: `1px solid ${C.headerBorder}`, mr: 3 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: "10px",
                background: "linear-gradient(135deg, #0d9488, #0f766e)",
                display: "grid", placeItems: "center",
                boxShadow: "0 2px 8px rgba(13,148,136,0.4)",
              }}>
                <HealthAndSafety sx={{ fontSize: 20, color: "#fff" }} />
              </Box>
              <Box>
                <Typography sx={{ fontFamily: F.sans, fontWeight: 700, fontSize: 13, color: C.onDark, lineHeight: 1.2 }}>{t('ebolaDashboard.brandName')}</Typography>
                <Typography sx={{ fontFamily: F.mono, fontSize: 9, color: C.onDarkMuted }}>{t('ebolaDashboard.brandMotto')}</Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Typography sx={{ fontFamily: F.sans, fontWeight: 700, fontSize: 17, color: C.onDark, lineHeight: 1.2 }}>
                {t('ebolaDashboard.title')}
              </Typography>
              <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.onDarkMuted, mt: 0.2 }}>
                {t('ebolaDashboard.subtitle')}
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }} />

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {[
                { icon: <Public sx={{ fontSize: 15, color: C.teal }} />, line1: t('ebolaDashboard.ministry'), line2: t('ebolaDashboard.ministrySub') },
                { icon: <HealthAndSafety sx={{ fontSize: 15, color: C.teal }} />, line1: t('ebolaDashboard.cousp'), line2: t('ebolaDashboard.couspSub') },
              ].map((b, i) => (
                <Box key={i} sx={{
                  display: "flex", alignItems: "center", gap: 1,
                  px: 1.5, py: 0.8,
                  border: `1px solid ${C.headerBorder}`,
                  borderRadius: "8px",
                  background: "rgba(255,255,255,0.04)",
                }}>
                  {b.icon}
                  <Box>
                    <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.onDark, lineHeight: 1.2 }}>{b.line1}</Typography>
                    <Typography sx={{ fontFamily: F.mono, fontSize: 9, color: C.onDarkMuted, lineHeight: 1.2 }}>{b.line2}</Typography>
                  </Box>
                </Box>
              ))}

              <Box sx={{ px: 1.5, py: 0.8, border: `1px solid ${C.headerBorder}`, borderRadius: "8px", background: "rgba(255,255,255,0.04)" }}>
                <Typography sx={{ fontFamily: F.mono, fontSize: 11, color: C.onDark, fontWeight: 600 }}>
                  {format(new Date(), "MMM dd, yyyy")}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mt: 0.2 }}>
                  <LivePulse />
                  <Typography sx={{ fontFamily: F.mono, fontSize: 9, color: C.onDarkMuted }}>
                    {format(new Date(), "HH:mm")} {t('ebolaDashboard.timezone')} &nbsp;·&nbsp; {t('ebolaDashboard.systemTime')}
                  </Typography>
                </Box>
              </Box>

              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel sx={{ color: C.onDarkMuted, fontSize: 12, "&.Mui-focused": { color: C.teal } }}>{t('lang.switchLanguage')}</InputLabel>
                <Select
                  value={lang}
                  label={t('lang.switchLanguage')}
                  onChange={(e) => handleLangChange(e.target.value)}
                  sx={{
                    color: C.onDark,
                    fontFamily: F.mono,
                    fontSize: 12,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: C.headerBorder },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: C.teal },
                    "& .MuiSvgIcon-root": { color: C.onDarkMuted },
                  }}
                >
                  <MenuItem value="en">{t('lang.en')}</MenuItem>
                  <MenuItem value="fr">{t('lang.fr')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>

        {/* ══ PAGE CONTENT ════════════════════════════════════════ */}
        <Box sx={{ maxWidth: 1600, mx: "auto", px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 2.5 }}>

          {/* ── FILTER BAR ─────────────────────────────────────── */}
          <Box sx={{ ...card(), px: 2.5, py: 1.8, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FilterList sx={{ fontSize: 15, color: C.muted }} />
              <Typography sx={{ fontFamily: F.sans, fontSize: 12, fontWeight: 600, color: C.sub }}>{t('ebolaDashboard.filters')}</Typography>
            </Box>

            <FormControl size="small" sx={{ minWidth: 190 }}>
              <DatePicker
                label={t('ebolaDashboard.dateRange')}
                value={startDate}
                onChange={(d) => d && setStartDate(d)}
                slotProps={{ textField: { size: "small", sx: { "& .MuiInputBase-root": { fontFamily: F.mono, fontSize: 12, borderRadius: "8px" } } } }}
              />
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ fontFamily: F.mono, fontSize: 12 }}>{t('ebolaDashboard.portOfEntry')}</InputLabel>
              <Select value={selectedPort} label={t('ebolaDashboard.portOfEntry')} onChange={(e) => setSelectedPort(e.target.value)} sx={{ fontFamily: F.mono, fontSize: 12, borderRadius: "8px" }}>
                {portOptions.map((p) => (
                  <MenuItem key={p} value={p} sx={{ fontFamily: F.mono, fontSize: 12 }}>{p === "ALL" ? t('ebolaDashboard.allPorts') : p}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ fontFamily: F.mono, fontSize: 12 }}>{t('ebolaDashboard.riskLevel')}</InputLabel>
              <Select value={selectedRisk} label={t('ebolaDashboard.riskLevel')} onChange={(e) => setSelectedRisk(e.target.value)} sx={{ fontFamily: F.mono, fontSize: 12, borderRadius: "8px" }}>
                {["ALL", "High Risk", "Moderate", "Low Risk"].map((r) => (
                  <MenuItem key={r} value={r} sx={{ fontFamily: F.mono, fontSize: 12 }}>{r === "ALL" ? t('ebolaDashboard.allRiskLevels') : r}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
              <Button variant="outlined" size="small" onClick={fetchData} startIcon={<Refresh sx={{ fontSize: 14 }} />}
                sx={{ fontFamily: F.sans, fontSize: 12, textTransform: "none", borderColor: C.cardBorder, color: C.sub, borderRadius: "8px", "&:hover": { borderColor: C.teal, color: C.teal, background: C.tealLt } }}>
                {t('ebolaDashboard.resetFilters')}
              </Button>
              <Button variant="contained" size="small" onClick={exportData} startIcon={<Download sx={{ fontSize: 14 }} />}
                sx={{ fontFamily: F.sans, fontSize: 12, textTransform: "none", background: `linear-gradient(135deg, ${C.teal}, ${C.tealDk})`, borderRadius: "8px", boxShadow: "0 2px 8px rgba(13,148,136,0.35)", "&:hover": { background: `linear-gradient(135deg, ${C.tealDk}, #085e58)` } }}>
                {t('ebolaDashboard.exportReport')}
              </Button>
            </Box>
          </Box>

          {/* ── KPI CARDS ───────────────────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 2 }}>
            {KPI_CONFIG.map((cfg) => {
              const IconComp = cfg.icon;
              const val  = summary[cfg.key] ?? 0;
              const pct  = (cfg as any).pct_key   ? summary[(cfg as any).pct_key]   : null;
              const delt = (cfg as any).delta_key ? summary[(cfg as any).delta_key] : null;
              const totalPorts = (cfg as any).total_key ? (summary[(cfg as any).total_key] ?? 0) : null;
              const isActive = activeKpi === cfg.tKey;
              return (
                <Box key={cfg.tKey} onClick={() => setActiveKpi(isActive ? null : cfg.tKey)} sx={{
                  ...card(),
                  p: 2.5, cursor: "pointer",
                  borderTop: `3px solid ${cfg.color}`,
                  outline: isActive ? `2px solid ${cfg.color}` : "2px solid transparent",
                  outlineOffset: "2px",
                  transition: "all .22s cubic-bezier(.4,0,.2,1)",
                  "&:hover": { transform: "translateY(-3px)", boxShadow: S.lg },
                  ...(isActive && { boxShadow: S.lg }),
                }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                    <Typography sx={{ fontFamily: F.sans, fontSize: 11, fontWeight: 500, color: C.sub, lineHeight: 1.4, maxWidth: "75%" }}>
                      {t(cfg.tKey)}
                    </Typography>
                    <Box sx={{ width: 32, height: 32, borderRadius: "9px", background: cfg.bg, display: "grid", placeItems: "center", color: cfg.color, flexShrink: 0 }}>
                      <IconComp sx={{ fontSize: 16 }} />
                    </Box>
                  </Box>

                  <Typography sx={{ fontFamily: F.mono, fontSize: 28, fontWeight: 800, color: C.ink, lineHeight: 1, mb: 1 }}>
                    {loading ? <Skeleton width={60} height={32} /> : <AnimNumber value={val} />}
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                    {delt != null && (
                      <>
                        {delt >= 0
                          ? <TrendingUp   sx={{ fontSize: 12, color: C.green }} />
                          : <TrendingDown sx={{ fontSize: 12, color: C.red   }} />}
                        <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: delt >= 0 ? C.green : C.red, fontWeight: 600 }}>
                          {delt >= 0 ? "+" : ""}{delt}%
                        </Typography>
                      </>
                    )}
                    {pct != null && (
                      <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.muted }}>{pct}{t(cfg.suffixKey)}</Typography>
                    )}
                    {delt == null && pct == null && totalPorts != null && (
                      <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.muted }}>
                        {lang === "fr"
                          ? `sur ${numFmt(totalPorts)} au total`
                          : `of ${numFmt(totalPorts)} Total`}
                      </Typography>
                    )}
                    {delt == null && pct == null && totalPorts == null && cfg.suffixKey && (
                      <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.muted }}>
                        {t(cfg.suffixKey)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* ── MAP + PORT TABLE ────────────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1.35fr 1fr", gap: 2.5 }}>

            <Box sx={{ ...card(), overflow: "hidden" }}>
              <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${C.cardBorder}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <SectionTitle title={t('ebolaDashboard.sections.screeningsByPort')} />
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {[
                    { color: C.green, label: t('ebolaDashboard.legend.lowRisk')      },
                    { color: C.amber, label: t('ebolaDashboard.legend.moderateRisk') },
                    { color: C.red,   label: t('ebolaDashboard.legend.highRisk')     },
                    { color: C.muted, label: t('ebolaDashboard.legend.portOfEntry')  },
                  ].map((l) => (
                    <Box key={l.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <FiberManualRecord sx={{ fontSize: 9, color: l.color }} />
                      <Typography sx={{ fontFamily: F.sans, fontSize: 10, color: C.sub }}>{l.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box sx={{ height: 480 }}>
                <GeoDashboardMap
                  selectedCountry={selectedCountry}
                  points={(data?.country_distribution || []).map((c: any) => ({
                    country: c.country,
                    count: c.count,
                  }))}
                />
              </Box>
            </Box>

            <Box sx={{ ...card(), overflow: "hidden" }}>
              <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${C.cardBorder}` }}>
                <SectionTitle title={t('ebolaDashboard.sections.highRiskAlertsByPort')} />
              </Box>

              <Box sx={{ height: 480, display: "flex", flexDirection: "column" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "24px 1fr 72px 60px 80px 90px", px: 2, py: 1, background: C.pageBg, borderBottom: `1px solid ${C.cardBorder}`, gap: 1, flexShrink: 0 }}>
                  {[
                    t('ebolaDashboard.table.number'),
                    t('ebolaDashboard.table.portOfEntry'),
                    t('ebolaDashboard.table.screened'),
                    t('ebolaDashboard.table.cleared'),
                    t('ebolaDashboard.table.highRisk'),
                    t('ebolaDashboard.table.status'),
                  ].map((h) => (
                    <Typography key={h} sx={{ fontFamily: F.mono, fontSize: 9, fontWeight: 700, color: C.muted, letterSpacing: 0.5 }}>{h}</Typography>
                  ))}
                </Box>

                <Box sx={{ flex: 1, overflowY: "auto" }}>
                  {loading && !data
                    ? Array.from({ length: 7 }).map((_, i) => (
                        <Box key={i} sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${C.divider}` }}>
                          <Skeleton height={20} />
                        </Box>
                      ))
                    : (data?.ports || []).map((port: any, idx: number) => {
                        const name = port.port_name || port.port;
                        const st   = port.status    || "Low Risk";
                        return (
                          <Box key={name + idx} sx={{
                            display: "grid", gridTemplateColumns: "24px 1fr 72px 60px 80px 90px",
                            px: 2, py: 1.5, borderBottom: `1px solid ${C.divider}`,
                            gap: 1, alignItems: "center",
                            transition: "background .15s",
                            "&:hover": { background: C.pageBg },
                          }}>
                            <Typography sx={{ fontFamily: F.mono, fontSize: 11, color: C.muted }}>{idx + 1}</Typography>
                            <Typography sx={{ fontFamily: F.sans, fontSize: 12, fontWeight: 600, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</Typography>
                            <Typography sx={{ fontFamily: F.mono, fontSize: 12, color: C.ink }}>{numFmt(port.screened)}</Typography>
                            <Typography sx={{ fontFamily: F.mono, fontSize: 12, color: C.green, fontWeight: 700 }}>{numFmt(port.cleared)}</Typography>
                            <Typography sx={{ fontFamily: F.mono, fontSize: 12, color: C.red, fontWeight: 700 }}>{numFmt(port.high_risk)}</Typography>
                            <RiskBadge label={st} />
                          </Box>
                        );
                      })
                  }
                </Box>

                {!loading && data?.ports && (
                  <Box sx={{ display: "grid", gridTemplateColumns: "24px 1fr 72px 60px 80px 90px", px: 2, py: 1.5, background: "#f8faff", borderTop: `2px solid ${C.cardBorder}`, gap: 1, alignItems: "center", flexShrink: 0 }}>
                    <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.muted }}>∑</Typography>
                    <Typography sx={{ fontFamily: F.sans, fontSize: 12, fontWeight: 700, color: C.ink }}>{t('ebolaDashboard.table.total')}</Typography>
                    <Typography sx={{ fontFamily: F.mono, fontSize: 12, fontWeight: 700, color: C.ink }}>{numFmt(summary.total_screenings)}</Typography>
                    <Typography sx={{ fontFamily: F.mono, fontSize: 12, fontWeight: 700, color: C.green }}>{numFmt(summary.cleared)}</Typography>
                    <Typography sx={{ fontFamily: F.mono, fontSize: 12, fontWeight: 700, color: C.red }}>{numFmt(summary.high_risk)}</Typography>
                    <Typography sx={{ fontFamily: F.mono, fontSize: 11, color: C.muted }}>—</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* ── CHARTS ROW ──────────────────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1.5fr 1fr", gap: 2.5 }}>

            <Box sx={{ ...card(), p: 2.5 }}>
              <SectionTitle title={t('ebolaDashboard.sections.screeningsTrend')} sub={t('ebolaDashboard.sections.screeningsTrendSub')}
                right={trendDelta != null && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.2, py: 0.4, borderRadius: "20px", background: trendDelta >= 0 ? C.greenLt : C.redLt }}>
                    {trendDelta >= 0 ? <TrendingUp sx={{ fontSize: 12, color: C.green }} /> : <TrendingDown sx={{ fontSize: 12, color: C.red }} />}
                    <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: trendDelta >= 0 ? C.green : C.red, fontWeight: 700 }}>
                      {trendDelta >= 0 ? "+" : ""}{trendDelta}%
                    </Typography>
                  </Box>
                )}
              />
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={trendData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gBlue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C.blue} stopOpacity={0.18} />
                      <stop offset="95%" stopColor={C.blue} stopOpacity={0}    />
                    </linearGradient>
                    <linearGradient id="gRed" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C.red} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={C.red} stopOpacity={0}    />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.divider} vertical={false} />
                  <XAxis dataKey="date" tick={{ fontFamily: F.mono, fontSize: 9, fill: C.muted }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontFamily: F.mono, fontSize: 9, fill: C.muted }} tickLine={false} axisLine={false} />
                  <Tooltip content={<ChartTip />} />
                  <Area type="monotone" dataKey="screenings"       name={t('ebolaDashboard.chartSeries.totalScreenings')} stroke={C.blue} strokeWidth={2.5} fill="url(#gBlue)" dot={false} />
                  <Area type="monotone" dataKey="high_risk_alerts" name={t('ebolaDashboard.chartSeries.highRiskAlerts')} stroke={C.red}  strokeWidth={2}   fill="url(#gRed)"  dot={false} strokeDasharray="4 2" />
                  <Legend wrapperStyle={{ fontFamily: F.mono, fontSize: 9, paddingTop: 8 }} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>

            <Box sx={{ ...card(), p: 2.5 }}>
              <SectionTitle title={t('ebolaDashboard.sections.outcomeDistribution')} />
              <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                {[
                  { c: C.green, l: t('ebolaDashboard.chartSeries.passedCleared')   },
                  { c: C.red,   l: t('ebolaDashboard.chartSeries.highRisk')         },
                  { c: C.amber, l: t('ebolaDashboard.chartSeries.referredFollowup') },
                ].map((item) => (
                  <Box key={item.l} sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "3px", background: item.c }} />
                    <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.sub }}>{item.l}</Typography>
                  </Box>
                ))}
              </Box>
              <ResponsiveContainer width="100%" height={215}>
                <ReBarChart data={outcomeByPort} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                  <XAxis type="number" tick={{ fontFamily: F.mono, fontSize: 9, fill: C.muted }} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="port" tick={{ fontFamily: F.mono, fontSize: 9, fill: C.muted }} tickLine={false} axisLine={false} width={80} />
                  <Tooltip content={<ChartTip />} />
                  <Bar dataKey="cleared"   name={t('ebolaDashboard.chartSeries.cleared')}   stackId="a" fill={C.green} />
                  <Bar dataKey="high_risk" name={t('ebolaDashboard.chartSeries.highRisk')} stackId="a" fill={C.red}   />
                  <Bar dataKey="follow_up" name={t('ebolaDashboard.chartSeries.followUp')} stackId="a" fill={C.amber} radius={[0, 4, 4, 0]} />
                </ReBarChart>
              </ResponsiveContainer>
              <Typography sx={{ fontFamily: F.mono, fontSize: 9, color: C.muted, mt: 1, textAlign: "center" }}>{t('ebolaDashboard.sections.percentageOfTotal')}</Typography>
            </Box>

            <Box sx={{ ...card(), p: 2.5, display: "flex", flexDirection: "column" }}>
              <SectionTitle title={t('ebolaDashboard.sections.riskLevelSummary')} right={
                <Typography sx={{ fontFamily: F.mono, fontSize: 11, color: C.muted }}>{numFmt(summary.total_screenings)} total</Typography>
              } />
              <Box sx={{ flex: 1, position: "relative" }}>
                <ResponsiveContainer width="100%" height={190}>
                  <PieChart>
                    <Pie data={riskChartData} dataKey="value" nameKey="name" innerRadius={58} outerRadius={90} paddingAngle={3} strokeWidth={0}>
                      {riskChartData.map((e: any) => <Cell key={e.name} fill={e.color} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", textAlign: "center", pointerEvents: "none" }}>
                  <Typography sx={{ fontFamily: F.mono, fontSize: 24, fontWeight: 800, color: C.ink, lineHeight: 1 }}>
                    {numFmt(summary.total_screenings || 0)}
                  </Typography>
                  <Typography sx={{ fontFamily: F.sans, fontSize: 10, color: C.muted }}>{t('common.total')}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: "grid", gap: 1, mt: 1 }}>
                {[
                  { label: t('ebolaDashboard.riskLevels.lowRisk'),      color: C.green, valKey: "low_risk"  },
                  { label: t('ebolaDashboard.riskLevels.moderateRisk'), color: C.amber, valKey: "moderate"  },
                  { label: t('ebolaDashboard.riskLevels.highRisk'),     color: C.red,   valKey: "high_risk" },
                ].map((item) => {
                  const v  = summary[item.valKey] ?? riskChartData.find((r: any) => r.name?.toLowerCase().includes(item.valKey.replace("_", " ")))?.value ?? 0;
                  const pc = summary.total_screenings ? Math.round((v / summary.total_screenings) * 1000) / 10 : 0;
                  return (
                    <Box key={item.label} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "3px", background: item.color }} />
                        <Typography sx={{ fontFamily: F.sans, fontSize: 11, color: C.ink }}>{item.label}</Typography>
                      </Box>
                      <Typography sx={{ fontFamily: F.mono, fontSize: 11, color: C.sub }}>
                        {numFmt(v)} <span style={{ color: C.muted }}>({pc}%)</span>
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* ── OPS SNAPSHOT + ALERTS ───────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1.6fr", gap: 2.5 }}>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              {[
                { label: t('ebolaDashboard.ops.teamsDeployed'),        icon: PersonOutline, color: C.blue,  val: snap.teams_deployed        ?? summary.active_ports   ?? 0,  sub: t('ebolaDashboard.ops.acrossPorts', { count: summary.active_ports ?? 0 }) },
                { label: t('ebolaDashboard.ops.isolationReferrals'),   icon: GppBad,        color: C.red,   val: snap.isolation_referrals   ?? summary.suspected_cases ?? 0, sub: t('ebolaDashboard.ops.today') },
                { label: t('ebolaDashboard.ops.responseTimeAvg'),      icon: Assessment,    color: C.green, val: `${snap.response_time_avg  ?? 18} ${t('ebolaDashboard.ops.minUnit')}`,    sub: t('ebolaDashboard.ops.targetResponseTime') },
                { label: t('ebolaDashboard.ops.dataSyncStatus'),       icon: WifiTethering, color: C.teal,  val: snap.sync_status           || "Live",                       sub: t('ebolaDashboard.ops.allSystemsConnected'), live: true },
              ].map((item) => {
                const IconComp = item.icon;
                return (
                  <Box key={item.label} sx={{ ...card(), p: 2.5, borderLeft: `3px solid ${item.color}`, transition: "box-shadow .2s", "&:hover": { boxShadow: S.md } }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                      <Typography sx={{ fontFamily: F.sans, fontSize: 11, fontWeight: 600, color: C.sub, lineHeight: 1.4 }}>{item.label}</Typography>
                      <Box sx={{ color: item.color }}><IconComp sx={{ fontSize: 18 }} /></Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      {(item as any).live && <LivePulse color={item.color} />}
                      <Typography sx={{ fontFamily: F.mono, fontSize: 26, fontWeight: 800, color: C.ink, lineHeight: 1 }}>
                        {typeof item.val === "number" ? <AnimNumber value={item.val} /> : item.val}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.muted }}>{item.sub}</Typography>
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ ...card(), display: "flex", flexDirection: "column", overflow: "hidden", maxHeight: 380 }}>
              <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${C.cardBorder}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: "8px", background: C.redLt, display: "grid", placeItems: "center" }}>
                    <NotificationsNone sx={{ fontSize: 15, color: C.red }} />
                  </Box>
                  <Typography sx={{ fontFamily: F.sans, fontWeight: 700, fontSize: 14, color: C.ink }}>{t('ebolaDashboard.alerts.title')}</Typography>
                  {(data?.latest_alerts?.length > 0) && (
                    <Box sx={{ width: 20, height: 20, borderRadius: "50%", background: C.red, display: "grid", placeItems: "center" }}>
                      <Typography sx={{ fontFamily: F.mono, fontSize: 9, color: "#fff", fontWeight: 700 }}>{data.latest_alerts.length}</Typography>
                    </Box>
                  )}
                </Box>
                <Typography sx={{ fontFamily: F.mono, fontSize: 11, color: C.teal, cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>{t('ebolaDashboard.alerts.viewAll')}</Typography>
              </Box>

              <Box sx={{ flex: 1, overflowY: "auto" }}>
                {loading
                  ? Array.from({ length: 4 }).map((_, i) => (
                      <Box key={i} sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${C.divider}` }}>
                        <Skeleton height={48} sx={{ borderRadius: 1 }} />
                      </Box>
                    ))
                  : (data?.latest_alerts || []).map((alert: any, idx: number) => {
                      const rl     = alert.risk_level || "Low Risk";
                      const isHigh = rl.toLowerCase().includes("high");
                      const isMod  = rl.toLowerCase().includes("mod");
                      return (
                        <Box key={idx} sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${C.divider}`, display: "flex", alignItems: "flex-start", gap: 1.5, transition: "background .15s", "&:hover": { background: C.pageBg } }}>
                          <Box sx={{ mt: 0.2, width: 28, height: 28, borderRadius: "8px", flexShrink: 0, background: isHigh ? C.redLt : isMod ? C.amberLt : C.greenLt, display: "grid", placeItems: "center" }}>
                            {isHigh ? <WarningAmber sx={{ fontSize: 14, color: C.red }} /> : isMod ? <ErrorOutline sx={{ fontSize: 14, color: C.amber }} /> : <CheckCircle sx={{ fontSize: 14, color: C.green }} />}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.3 }}>
                              <Typography sx={{ fontFamily: F.sans, fontSize: 12, fontWeight: 700, color: C.ink }}>{alert.port}</Typography>
                              <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.muted, flexShrink: 0, ml: 1 }}>
                                {alert.timestamp ? format(new Date(alert.timestamp), "HH:mm") : "—"}
                              </Typography>
                            </Box>
                            <Typography sx={{ fontFamily: F.sans, fontSize: 11, color: C.sub, lineHeight: 1.4, mb: 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {alert.alert_text || alert.alert_reason}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                              <RiskBadge label={rl} />
                              {alert.country_of_origin && <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.muted }}>{alert.country_of_origin}</Typography>}
                              {alert.temperature && <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.muted }}>{alert.temperature}°C</Typography>}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })
                }
              </Box>
            </Box>
          </Box>

          {/* ── ANALYTICS ROW ───────────────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1.5fr 1fr", gap: 2.5 }}>

            <Box sx={{ ...card(), p: 2.5 }}>
              <SectionTitle title={t('ebolaDashboard.sections.countryAnalysis')} />
              <Box sx={{ display: "grid", gap: 1.4 }}>
                {(countryData || []).map((item: any, idx: number) => {
                  const palette = [C.blue, C.teal, C.purple, C.amber, C.red, C.green];
                  const pct = summary.total_screenings ? Math.round((item.count / summary.total_screenings) * 100) : 0;
                  return (
                    <Box key={item.country || idx}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.6 }}>
                        <Typography sx={{ fontFamily: F.sans, fontSize: 12, color: C.ink }}>{item.country || "Unknown"}</Typography>
                        <Typography sx={{ fontFamily: F.mono, fontSize: 12, color: C.sub, fontWeight: 600 }}>{numFmt(item.count)}</Typography>
                      </Box>
                      <Box sx={{ height: 6, borderRadius: 3, background: C.divider, overflow: "hidden" }}>
                        <Box sx={{ height: "100%", borderRadius: 3, background: palette[idx % palette.length], width: `${pct}%`, transition: "width 1s cubic-bezier(.4,0,.2,1)" }} />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box sx={{ ...card(), p: 2.5 }}>
              <SectionTitle title={t('ebolaDashboard.sections.symptomsDistribution')} />
              <ResponsiveContainer width="100%" height={240}>
                <ReBarChart data={symptomData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gSymp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor={C.blue} stopOpacity={1}   />
                      <stop offset="100%" stopColor={C.teal} stopOpacity={0.8} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.divider} vertical={false} />
                  <XAxis dataKey="symptom" tick={{ fontFamily: F.mono, fontSize: 9, fill: C.muted }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontFamily: F.mono, fontSize: 9, fill: C.muted }} tickLine={false} axisLine={false} />
                  <Tooltip content={<ChartTip />} />
                  <Bar dataKey="count" name={t('ebolaDashboard.chartSeries.cases')} fill="url(#gSymp)" radius={[5, 5, 0, 0]} />
                </ReBarChart>
              </ResponsiveContainer>
            </Box>

            <Box sx={{ ...card(), p: 2.5, display: "flex", flexDirection: "column" }}>
              <SectionTitle title={t('ebolaDashboard.sections.vaccinationCompliance')} />
              <Box sx={{ flex: 1, position: "relative" }}>
                <ResponsiveContainer width="100%" height={190}>
                  <PieChart>
                    <Pie data={vaccData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={88} paddingAngle={4} strokeWidth={0}>
                      {vaccData.map((e: any) => (
                        <Cell key={e.name} fill={e.name?.toLowerCase().includes("vacc") ? C.green : C.red} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", textAlign: "center", pointerEvents: "none" }}>
                  <Typography sx={{ fontFamily: F.mono, fontSize: 20, fontWeight: 800, color: C.green, lineHeight: 1 }}>{vaccPct}%</Typography>
                  <Typography sx={{ fontFamily: F.sans, fontSize: 9, color: C.muted }}>{t('ebolaDashboard.vaccination.complianceLabel')}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: "grid", gap: 0.8 }}>
                {vaccData.map((item: any) => (
                  <Box key={item.name} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "3px", background: item.name?.toLowerCase().includes("vacc") ? C.green : C.red }} />
                      <Typography sx={{ fontFamily: F.sans, fontSize: 11, color: C.ink }}>{item.name}</Typography>
                    </Box>
                    <Typography sx={{ fontFamily: F.mono, fontSize: 11, color: C.sub, fontWeight: 600 }}>{numFmt(item.value)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {error && (
            <Box sx={{ ...card(), p: 2.5, borderLeft: `4px solid ${C.red}`, background: C.redLt }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <ErrorOutline sx={{ fontSize: 18, color: C.red }} />
                <Typography sx={{ fontFamily: F.mono, fontSize: 12, color: C.red }}>{error}</Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ height: 20 }} />
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default DashboardPage;
